import React, { ReactElement, useRef, useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    LinearProgress,
    Typography,
    alpha,
} from '@mui/material';
import { Close, CloudUpload, Image } from '@mui/icons-material';
import { uploadProfilePhotos } from 'api';
import { useSelector } from 'react-redux';
import { authSelector } from 'redux/reducers/auth';
import { pushMessage } from 'redux/reducers/messages';
import { useAppDispatch } from 'redux/store';
import { Group } from 'utils';
import S3Upload from 'utils/S3Upload';
import useTheme from '@mui/system/useTheme';
import ProfilePhotoMatchReview, { PhotoMatchResult } from './ProfilePhotoMatchReview';

interface ProfilePhotoUploadDialogProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    group: Group;
}

export default function ProfilePhotoUploadDialog({
    open,
    onClose,
    onSuccess,
    group,
}: ProfilePhotoUploadDialogProps): ReactElement {
    const dispatch = useAppDispatch();
    const { currentUser } = useSelector(authSelector);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [uploading, setUploading] = useState<boolean>(false);
    const [uploadProgress, setUploadProgress] = useState<number>(0);
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [matchResult, setMatchResult] = useState<PhotoMatchResult | null>(null);
    const [showMatchReview, setShowMatchReview] = useState<boolean>(false);
    const theme = useTheme();

    const glassStyle = {
        background: alpha(theme.palette.background.paper, 0.8),
        backdropFilter: 'blur(10px)',
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    };

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files) return;

        // Filter to only image files
        const imageFiles = Array.from(files).filter((file) => {
            const extension = file.name.split('.').pop()?.toLowerCase();
            return ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(extension || '');
        });

        if (imageFiles.length === 0) {
            dispatch(
                pushMessage({
                    status: 'error',
                    message:
                        'No valid image files found. Please select PNG, JPG, GIF, or WEBP files.',
                }),
            );
            return;
        }

        setSelectedFiles(imageFiles);
    };

    const handleUpload = async () => {
        if (selectedFiles.length === 0) {
            dispatch(
                pushMessage({
                    status: 'error',
                    message: 'Please select photos to upload',
                }),
            );
            return;
        }

        if (!currentUser) {
            dispatch(
                pushMessage({
                    status: 'error',
                    message: 'You must be logged in to upload photos',
                }),
            );
            return;
        }

        setUploading(true);
        setUploadProgress(0);

        try {
            // Upload files to S3 one by one
            console.log('Starting S3 upload for', selectedFiles.length, 'files...');
            const uploadedPhotos: Array<{ s3_path: string; original_filename: string }> = [];
            let completedCount = 0;

            for (const file of selectedFiles) {
                console.log(`Starting upload for: ${file.name} (${formatFileSize(file.size)})`);

                const s3Path = await new Promise<string>((resolve, reject) => {
                    const uploader = new S3Upload(undefined, 'tmp/profile-photos/');

                    uploader.onStarted = (fileName: string) => {
                        console.log(`S3 upload started for ${file.name} as ${fileName}`);
                    };

                    uploader.onUploadInitiated = (fileName: string, s3UploadId?: string) => {
                        console.log(`S3 upload initiated: ${fileName}, uploadId: ${s3UploadId}`);
                    };

                    uploader.onBeforeSigner = (fileName: string, xhr: XMLHttpRequest) => {
                        console.log(`Requesting signature for ${fileName}`);
                        xhr.setRequestHeader('Accept', 'application/json');
                        xhr.setRequestHeader('Authorization', `Bearer ${currentUser.accessToken}`);
                    };

                    uploader.onProgress = (
                        fileName: string,
                        originalFileName: string,
                        progress: number,
                    ) => {
                        console.log(
                            `Upload progress for ${originalFileName}: ${Math.round(progress)}%`,
                        );
                    };

                    uploader.onComplete = (fileName: string) => {
                        console.log(`✅ Successfully uploaded ${file.name} to ${fileName}`);
                        completedCount += 1;
                        setUploadProgress((completedCount / selectedFiles.length) * 100);
                        resolve(fileName);
                    };

                    uploader.onError = (fileName: string, msg: string) => {
                        console.error(
                            `❌ Upload error for ${file.name}:`,
                            msg,
                            `\nFile: ${fileName}`,
                        );
                        reject(new Error(`Failed to upload ${file.name}: ${msg}`));
                    };

                    try {
                        uploader.uploadToS3(file);
                    } catch (err) {
                        console.error(`Exception starting upload for ${file.name}:`, err);
                        reject(err);
                    }
                });

                uploadedPhotos.push({
                    s3_path: s3Path,
                    original_filename: file.name,
                });
            }

            console.log('All files uploaded to S3:', uploadedPhotos);

            // Call backend to match photos to members
            console.log('Calling backend API to match photos...');
            const response = await uploadProfilePhotos(group, uploadedPhotos);
            console.log('Backend response:', response);

            // Detailed logging of response structure
            console.log('=== DETAILED RESPONSE ANALYSIS ===');
            console.log('Full response data:', JSON.stringify(response.data, null, 2));
            console.log('Response data keys:', Object.keys(response.data || {}));

            if (response.data?.matches && response.data.matches.length > 0) {
                console.log('First match object:', response.data.matches[0]);
                console.log(
                    'First match has thumbnail_url:',
                    'thumbnail_url' in response.data.matches[0],
                );
                console.log(
                    'First match thumbnail_url value:',
                    response.data.matches[0].thumbnail_url,
                );
                console.log(
                    'First match thumbnail_url type:',
                    typeof response.data.matches[0].thumbnail_url,
                );
            } else {
                console.log('No matches in response');
            }

            if (response.data?.multiple_matches && response.data.multiple_matches.length > 0) {
                console.log('First multiple match object:', response.data.multiple_matches[0]);
                console.log(
                    'First multiple match has thumbnail_url:',
                    'thumbnail_url' in response.data.multiple_matches[0],
                );
            } else {
                console.log('No multiple matches in response');
            }
            console.log('=== END RESPONSE ANALYSIS ===');

            if (response.data) {
                console.log('Setting match result and showing review dialog...');
                setMatchResult(response.data);
                setShowMatchReview(true);
                setUploading(false);
            }
        } catch (error: any) {
            console.error('Upload error details:', {
                error,
                message: error?.message,
                response: error?.response,
                responseData: error?.response?.data,
                status: error?.response?.status,
            });
            dispatch(
                pushMessage({
                    status: 'error',
                    message:
                        error?.response?.data?.message ||
                        error?.message ||
                        'Failed to upload photos. Please try again.',
                }),
            );
            setUploading(false);
        }
    };

    const handleClose = () => {
        if (!uploading) {
            setSelectedFiles([]);
            setMatchResult(null);
            setShowMatchReview(false);
            setUploadProgress(0);
            onClose();
        }
    };

    const handleMatchReviewClose = () => {
        setShowMatchReview(false);
        setMatchResult(null);
    };

    const handleConfirmSuccess = () => {
        setShowMatchReview(false);
        setMatchResult(null);
        setSelectedFiles([]);
        onSuccess();
        onClose();
    };

    if (showMatchReview && matchResult) {
        return (
            <ProfilePhotoMatchReview
                open={true}
                matchResult={matchResult}
                group={group}
                onClose={handleMatchReviewClose}
                onSuccess={handleConfirmSuccess}
            />
        );
    }

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`;
    };

    const totalSize = selectedFiles.reduce((sum, file) => sum + file.size, 0);

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Import Profile Photos</Typography>
                    <IconButton onClick={handleClose} disabled={uploading}>
                        <Close />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ py: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Upload a folder of profile photos to automatically match them to members in{' '}
                        {group.name}. Photos should be named using the athlete&apos;s name (e.g.,
                        Andy_Ibanez.png or Ibanez_Andy.png).
                    </Typography>

                    {/* Upload Zone */}
                    <Box
                        sx={{
                            ...glassStyle,
                            p: 4,
                            textAlign: 'center',
                            cursor: uploading ? 'not-allowed' : 'pointer',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                borderColor: uploading ? 'divider' : 'primary.main',
                                background: uploading
                                    ? glassStyle.background
                                    : alpha(theme.palette.primary.main, 0.05),
                            },
                        }}
                        onClick={() => !uploading && fileInputRef.current?.click()}
                    >
                        <input
                            type="file"
                            ref={fileInputRef}
                            style={{ display: 'none' }}
                            multiple
                            accept="image/*"
                            /* @ts-ignore - webkitdirectory is not in TypeScript types */
                            {...({ webkitdirectory: '' } as any)}
                            onChange={handleFileSelect}
                            disabled={uploading}
                        />

                        <CloudUpload
                            sx={{
                                fontSize: 64,
                                color: 'primary.main',
                                mb: 2,
                                opacity: uploading ? 0.5 : 1,
                            }}
                        />

                        <Typography variant="h6" gutterBottom>
                            {selectedFiles.length > 0
                                ? `${selectedFiles.length} photos selected`
                                : 'Select Folder'}
                        </Typography>

                        <Typography variant="body2" color="text.secondary">
                            {selectedFiles.length > 0
                                ? `Total size: ${formatFileSize(totalSize)}`
                                : 'Click to choose a folder containing profile photos'}
                        </Typography>
                    </Box>

                    {/* File List */}
                    {selectedFiles.length > 0 && !uploading && (
                        <Box sx={{ ...glassStyle, p: 2, mt: 2, maxHeight: 200, overflow: 'auto' }}>
                            <Typography variant="subtitle2" gutterBottom>
                                Selected Files:
                            </Typography>
                            {selectedFiles.slice(0, 10).map((file, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        py: 0.5,
                                    }}
                                >
                                    <Image fontSize="small" color="action" />
                                    <Typography variant="body2" sx={{ flex: 1 }}>
                                        {file.name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {formatFileSize(file.size)}
                                    </Typography>
                                </Box>
                            ))}
                            {selectedFiles.length > 10 && (
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                                    ... and {selectedFiles.length - 10} more files
                                </Typography>
                            )}
                        </Box>
                    )}

                    {/* Upload Progress */}
                    {uploading && (
                        <Box sx={{ mt: 3 }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                Uploading and matching photos...
                            </Typography>
                            <LinearProgress
                                variant="determinate"
                                value={uploadProgress}
                                sx={{ height: 8, borderRadius: 1 }}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                {Math.round(uploadProgress)}%
                            </Typography>
                        </Box>
                    )}

                    {/* Actions */}
                    <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        <Button onClick={handleClose} disabled={uploading}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleUpload}
                            disabled={uploading || selectedFiles.length === 0}
                            startIcon={<CloudUpload />}
                        >
                            {uploading ? 'Uploading...' : 'Upload & Match'}
                        </Button>
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
}
